/**
 * Outline + hard drop-shadow subtitle styling ("white italic text, black
 * outline, black offset shadow").
 *
 * Wire contract (mirrored in ../autoclipper-backend src/lib/subtitleOutlineStyle.js):
 * - editor / `subtitle_config`: strokeEnabled, strokeColor, strokeWidth,
 *   shadowStyle, shadowOffsetX, shadowOffsetY, shadowSpread
 * - brand kit `data.subtitle`: the same fields in snake_case
 * - Remotion `lineStyle`: strokeColor, strokeWidth (0 = off), shadowStyle,
 *   shadowOffsetX, shadowOffsetY, shadowSpread
 * strokeWidth, the offsets and the spread are percentages of the subtitle font size, so
 * they survive the 720p design-pixel scaling and the brand-kit preview size.
 */

export type SubtitleShadowStyle = 'glow' | 'drop';

export interface SubtitleOutlineFields {
  strokeEnabled: boolean;
  strokeColor: string;
  /** % of font size. */
  strokeWidth: number;
  shadowStyle: SubtitleShadowStyle;
  /** % of font size. */
  shadowOffsetX: number;
  /** % of font size. */
  shadowOffsetY: number;
  /** Drop only: how much bigger than the (outlined) text the projected shadow is, % of font size. */
  shadowSpread: number;
}

export const DEFAULT_SUBTITLE_OUTLINE: SubtitleOutlineFields = {
  strokeEnabled: false,
  strokeColor: '#000000',
  strokeWidth: 10,
  shadowStyle: 'glow',
  shadowOffsetX: 8,
  shadowOffsetY: 8,
  shadowSpread: 0,
};

export const SUBTITLE_STROKE_WIDTH_MAX = 30;
export const SUBTITLE_SHADOW_OFFSET_MAX = 50;
export const SUBTITLE_SHADOW_SPREAD_MAX = 100;

export function pickSubtitleOutline(data: SubtitleOutlineFields): SubtitleOutlineFields {
  return {
    strokeEnabled: data.strokeEnabled,
    strokeColor: data.strokeColor,
    strokeWidth: data.strokeWidth,
    shadowStyle: data.shadowStyle,
    shadowOffsetX: data.shadowOffsetX,
    shadowOffsetY: data.shadowOffsetY,
    shadowSpread: data.shadowSpread,
  };
}

export interface SubtitleOutlineLineStyle {
  strokeColor?: string;
  strokeWidth: number;
  shadowStyle: SubtitleShadowStyle;
  shadowOffsetX: number;
  shadowOffsetY: number;
  shadowSpread: number;
}

export function subtitleOutlineLineStyle(data: SubtitleOutlineFields): SubtitleOutlineLineStyle {
  return {
    ...(data.strokeEnabled
      ? { strokeColor: data.strokeColor, strokeWidth: data.strokeWidth }
      : { strokeWidth: 0 }),
    shadowStyle: data.shadowStyle,
    shadowOffsetX: data.shadowOffsetX,
    shadowOffsetY: data.shadowOffsetY,
    shadowSpread: data.shadowSpread,
  };
}

export interface SubtitleOutlineKitFields {
  stroke_enabled: boolean;
  stroke_color: string;
  stroke_width: number;
  shadow_style: SubtitleShadowStyle;
  shadow_offset_x: number;
  shadow_offset_y: number;
  shadow_spread: number;
}

export function subtitleOutlineToKit(data: SubtitleOutlineFields): SubtitleOutlineKitFields {
  return {
    stroke_enabled: data.strokeEnabled,
    stroke_color: data.strokeColor,
    stroke_width: data.strokeWidth,
    shadow_style: data.shadowStyle,
    shadow_offset_x: data.shadowOffsetX,
    shadow_offset_y: data.shadowOffsetY,
    shadow_spread: data.shadowSpread,
  };
}

const num = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/** Reads a (possibly legacy / partial) brand-kit subtitle; missing fields use the defaults. */
export function subtitleOutlineFromKit(sub: object | null | undefined): SubtitleOutlineFields {
  const s = (sub ?? {}) as Record<string, unknown>;
  const d = DEFAULT_SUBTITLE_OUTLINE;
  return {
    strokeEnabled: typeof s.stroke_enabled === 'boolean' ? s.stroke_enabled : d.strokeEnabled,
    strokeColor: typeof s.stroke_color === 'string' && s.stroke_color ? s.stroke_color : d.strokeColor,
    strokeWidth: num(s.stroke_width, d.strokeWidth),
    shadowStyle: s.shadow_style === 'drop' || s.shadow_style === 'glow' ? s.shadow_style : d.shadowStyle,
    shadowOffsetX: num(s.shadow_offset_x, d.shadowOffsetX),
    shadowOffsetY: num(s.shadow_offset_y, d.shadowOffsetY),
    shadowSpread: num(s.shadow_spread, d.shadowSpread),
  };
}

export interface SubtitleTextShadowInput {
  fontSizePx: number;
  strokeColor?: string | null;
  /** % of font size. */
  strokeWidth?: number | null;
  shadowStyle?: SubtitleShadowStyle | null;
  shadowColor?: string | null;
  /** Glow: % of font size (legacy semantics). 0 = shadow off, for both styles. */
  shadowSize?: number | null;
  /** Drop only: % of font size. */
  shadowOffsetX?: number | null;
  shadowOffsetY?: number | null;
  /** Drop only: % of font size added around the (outlined) text. */
  shadowSpread?: number | null;
}

const px = (value: number) => `${Number(value.toFixed(2))}px`;

/** Upper bound of hard shadows one disk may emit, so a huge size stays cheap to render. */
const MAX_DISK_SHADOWS = 380;

/**
 * Hard shadows filling a disk of radius `r` around (cx, cy). The union of the
 * shifted glyphs is the text grown by `r` — an outline that sits OUTSIDE the
 * letters (unlike -webkit-text-stroke, which is centered on the glyph edge and
 * eats into thin strokes) and renders identically in headless Chrome.
 *
 * A single ring at radius `r` is enough while `r` is small next to the glyph
 * strokes; for large sizes (a projected shadow up to 100% of the font) the
 * shifted copies would drift apart and leave see-through gaps, so concentric
 * rings fill the interior. Ring spacing and point spacing are both `step` —
 * at most 7% of the font (thinner than a regular-weight stroke), widened only
 * when needed to stay within MAX_DISK_SHADOWS.
 */
function disk(color: string, r: number, cx: number, cy: number, fontSizePx: number): string[] {
  const step = Math.max(1, 0.07 * fontSizePx, r * Math.sqrt(Math.PI / MAX_DISK_SHADOWS));
  // Up to two steps a single ring suffices (the glyph itself covers the middle);
  // beyond that, evenly spaced rings from the center out to r, so no gap exceeds `step`.
  const rings = r <= 2 * step ? 1 : Math.ceil(r / step);
  const out: string[] = [];
  for (let k = rings; k >= 1; k -= 1) {
    const ringR = (r * k) / rings;
    // The outer ring alone shapes the visible edge, so it is sampled densely
    // (every ~3% of the font); inner rings only need to fill, at `step`.
    const spacing = k === rings && rings > 1 ? Math.max(1, 0.03 * fontSizePx) : step;
    const count = Math.max(16, Math.min(200, Math.ceil((2 * Math.PI * ringR) / spacing)));
    for (let i = 0; i < count; i += 1) {
      const angle = (2 * Math.PI * i) / count;
      out.push(`${color} ${px(cx + ringR * Math.cos(angle))} ${px(cy + ringR * Math.sin(angle))} 0px`);
    }
  }
  return out;
}

/**
 * CSS `text-shadow` for a subtitle line: optional outline, then either the
 * legacy blurred glow or a hard drop shadow of the outlined shape. The first
 * entry paints on top, so the outline always covers the shadow.
 */
export function buildSubtitleTextShadow(input: SubtitleTextShadowInput): string | null {
  const { fontSizePx, strokeColor, shadowColor } = input;
  if (!fontSizePx) return null;

  const strokePx = strokeColor ? (num(input.strokeWidth, 0) / 100) * fontSizePx : 0;
  const list: string[] = strokePx > 0 && strokeColor ? disk(strokeColor, strokePx, 0, 0, fontSizePx) : [];

  // Every surface encodes "shadow off" as shadowSize 0 (see the export
  // builders), so both styles require a size.
  const shadowSize = shadowColor ? num(input.shadowSize, 0) : 0;
  if (shadowSize && input.shadowStyle === 'drop') {
    const dx = (num(input.shadowOffsetX, 0) / 100) * fontSizePx;
    const dy = (num(input.shadowOffsetY, 0) / 100) * fontSizePx;
    const spreadPx = (Math.max(0, num(input.shadowSpread, 0)) / 100) * fontSizePx;
    // The projected shape is the outlined text grown by the spread.
    const radius = strokePx + spreadPx;
    if (dx !== 0 || dy !== 0 || radius > 0) {
      list.push(`${shadowColor} ${px(dx)} ${px(dy)} 0px`);
      if (radius > 0) list.push(...disk(shadowColor, radius, dx, dy, fontSizePx));
    }
  } else if (shadowSize) {
    // Legacy glow: one blurred copy per px of the configured size.
    const size = Math.round((shadowSize / 100) * fontSizePx);
    for (let index = 0; index < size; index += 1) list.push(`${shadowColor} 0px 0px ${index}px`);
  }

  return list.length ? list.join(', ') : null;
}

const OUTLINE_KEYS = Object.keys(DEFAULT_SUBTITLE_OUTLINE) as (keyof SubtitleOutlineFields)[];

/**
 * lineStyle overrides for a config that may carry only some (or none) of the
 * outline fields — e.g. a legacy saved config, or the brand-kit card preview,
 * which only forwards the keys a kit sets. No outline key → no override, so
 * legacy configs render exactly as before.
 */
export function subtitleOutlineLineStyleFromPartial(
  config: Partial<SubtitleOutlineFields> | null | undefined,
): Partial<SubtitleOutlineLineStyle> {
  if (!config || !OUTLINE_KEYS.some((key) => config[key] !== undefined)) return {};
  const merged = { ...DEFAULT_SUBTITLE_OUTLINE };
  OUTLINE_KEYS.forEach((key) => {
    if (config[key] !== undefined) (merged as Record<string, unknown>)[key] = config[key];
  });
  return subtitleOutlineLineStyle(merged);
}
